import { Radii, Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'

// Color tokens for CallButton. Icon colors follow each variant's own surface
// palette, not brand.contrast — partner themes (e.g. Zocdoc yellow) redefine
// brand.contrast to black, which must not darken the green/red call icons.
const CallButtonColors = {
  start: {
    background: asChakraVariable('green.solid'),
    hover: asChakraVariable('green.emphasized'),
    active: asChakraVariable('green.emphasized'),
    icon: asChakraVariable('green.contrast'),
  },
  stop: {
    background: asChakraVariable('red.solid'),
    hover: asChakraVariable('red.emphasized'),
    icon: asChakraVariable('red.contrast'),
  },
  disabled: {
    background: asChakraVariable('fg.muted'),
    // Neutral gray surface, so no palette contrast token applies; plain white
    // matches how this icon has always rendered in both color modes.
    icon: asChakraVariable('white'),
  },
}

export enum CallButtonVariants {
  Start = 'start',
  Stop = 'stop',
  Disabled = 'disabled',
}

const callButtonParts = ['button', 'icon', 'tooltip'] as const
const helpers = createMultiStyleConfigHelpers(callButtonParts)

export const CallButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    button: {
      boxSize: Size.full,
      borderRadius: Radii.full,
    },
    icon: {
      boxSize: '26px',
    },
  },
  sizes: {
    [Size.sm]: {
      button: {
        boxSize: '60px',
      },
    },
    [Size.md]: {
      button: {
        boxSize: '80px',
      },
    },
    [Size.lg]: {
      button: {
        boxSize: '100px',
      },
    },
    [Size.full]: {
      button: {
        boxSize: Size.full,
      },
    },
  },
  variants: {
    [CallButtonVariants.Start]: {
      button: {
        bg: CallButtonColors.start.background,
        _hover: {
          bg: CallButtonColors.start.hover,
        },
        _active: {
          bg: CallButtonColors.start.active,
        },
      },
      icon: {
        color: CallButtonColors.start.icon,
      },
    },
    [CallButtonVariants.Stop]: {
      button: {
        bg: CallButtonColors.stop.background,
        _hover: {
          bg: CallButtonColors.stop.hover,
        },
      },
      icon: {
        color: CallButtonColors.stop.icon,
      },
    },
    [CallButtonVariants.Disabled]: {
      button: {
        bg: CallButtonColors.disabled.background,
        _hover: {
          cursor: 'default',
        },
      },
      icon: {
        color: CallButtonColors.disabled.icon,
      },
      tooltip: {
        display: 'none',
      },
    },
  },
  defaultProps: {
    size: Size.full,
  },
})

export const useCallButtonStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(CallButtonStyle, props) as MultiStyleType<
    typeof callButtonParts
  >
}
